'use client';

import { PageShell } from '@/components/layout/page-shell';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { ImpersonateButton } from '@/features/admin/components/impersonate-button';
import { CreatorFollowButton } from '@/features/creator/components/CreatorFollowButton';
import { DmEntryButton } from '@/features/dm/components/DmEntryButton';
import { UnlockCta } from '@/features/freemium/components/unlock-cta';
import { canSeePaidDms } from '@/shared/lib/auth/permissions';
import { useAuthStore } from '@/shared/stores/auth-store';
import { getChannelHandle, cn } from '@/shared/utils/formatting';
import { getChoosePlanHrefForCreator } from '@/shared/utils/routes';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { AvatarAmbientGlow } from './AvatarAmbientGlow';
import type { CreatorHeaderProps } from './types';

const BANNER_FALLBACK_GRADIENT = 'linear-gradient(135deg, #1a0808 0%, #2a0f0f 40%, #0a0a12 100%)';

export function CreatorHeader({
  creator,
  isOwner,
  isGuest,
  stats,
  socialLinks,
  fallbackBanner,
}: CreatorHeaderProps) {
  const countriesRecorded = creator.countries_recorded ?? 0;
  const bannerSrc = creator.banner || fallbackBanner;
  const isFallbackBanner = !creator.banner && !!bannerSrc;
  const user = useAuthStore((s) => s.user);
  // Self-arming (FRONT-310): the dm fields aren't on the creator payload yet
  // (open BE ask) — the button appears automatically once they ship. Opted-out
  // and unpriced creators stay hidden with no explanation, per the handoff.
  const dmHandle = (getChannelHandle(creator) ?? '').replace(/^@/, '');
  const dmPrice = typeof creator.dm_price_coins === 'number' ? creator.dm_price_coins : null;
  const showDmEntry =
    !isGuest &&
    !isOwner &&
    canSeePaidDms(user) &&
    creator.dm_paid_enabled === true &&
    dmPrice !== null &&
    dmHandle.length > 0;

  return (
    <section className="relative">
      {/* Banner */}
      <div className="relative min-h-[120px] sm:min-h-[200px] overflow-hidden">
        {bannerSrc ? (
          <Image
            src={bannerSrc}
            alt=""
            fill
            className={cn('object-cover', isFallbackBanner && 'opacity-35 blur-sm')}
            loading="eager"
            fetchPriority="high"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: BANNER_FALLBACK_GRADIENT }} />
        )}
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: isFallbackBanner
              ? 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 60%, #000 100%)'
              : 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.6) 100%)',
          }}
        />
      </div>

      {/* Avatar — bridges banner and content */}
      <PageShell spacing="none" className="-mt-14">
        <div className="relative w-fit">
          {creator.dp && <AvatarAmbientGlow src={creator.dp} />}
          <Avatar
            src={creator.dp}
            alt={creator.name}
            fallback={creator.name}
            size="xxl"
            className="relative z-10 h-[112px] w-[112px] border-[3px] border-elevated shrink-0"
          />
        </div>
      </PageShell>

      {/* All info below banner */}
      <PageShell spacing="none" className="mt-3">
        {/* Row 1: Name + verified + action buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-2xl! sm:text-2xl! font-bold! text-text-primary truncate">
              {creator.name}
            </h1>
            <span className="shrink-0">
              <VerifiedBadge size={18} />
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isGuest && isOwner && (
              <Button
                variant="destructive"
                size="sm"
                className="bg-secondary btn btn-sm py-4.5! text-white/80 hover:border-white/50 hover:text-white hover:bg-white/10"
                asChild
              >
                <Link href="/studio/content">Manage videos</Link>
              </Button>
            )}

            {!isGuest && !isOwner && <CreatorFollowButton creator={creator} size="lg" />}

            {showDmEntry && dmPrice !== null && (
              <DmEntryButton href={`/messages/new/${dmHandle}`} priceCoins={dmPrice} />
            )}

            {!isGuest && (
              <ImpersonateButton
                userId={creator.user_id}
                handler={getChannelHandle(creator) ?? String(creator.user_id)}
              />
            )}
          </div>
        </div>

        {/* Row 2: Handler + stats */}
        <div className="flex flex-wrap items-center gap-3 mt-1">
          {getChannelHandle(creator) && (
            <span className="text-sm text-text-primary">@{getChannelHandle(creator)}</span>
          )}
          <span className="text-sm text-text-secondary">•</span>
          {stats.map((stat) => (
            <span
              key={stat.key}
              className="inline-flex items-center gap-1 text-sm text-text-secondary"
            >
              <span className="text-red-primary flex">{stat.icon}</span>
              <span>
                {stat.value} {stat.label}
              </span>
            </span>
          ))}
        </div>

        {/* Row 3: Bio — whitespace-pre-line preserves the \n authored in the
            admin Channel Description textarea (default p collapses newlines). */}
        {creator.description && (
          <p className="text-sm leading-relaxed text-text-secondary mt-3 max-w-2xl whitespace-pre-line">
            {creator.description}
          </p>
        )}

        {/* Row 4: Social + countries pill */}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.key}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center justify-center',
                    'text-white/40',
                    'transition-colors duration-200',
                    'hover:text-white/80',
                    '[&_svg]:w-4.5 [&_svg]:h-4.5'
                  )}
                  title={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          )}

          {countriesRecorded > 0 && (
            <Link
              href={`/globe?creator=${getChannelHandle(creator)}`}
              className={cn(
                'group/globe inline-flex items-center gap-2 rounded-[7px]',
                'px-3 py-1.5 pl-2',
                'bg-white/[0.04] border border-white/10 backdrop-blur-[4px]',
                'transition-all duration-200',
                'hover:bg-[rgba(204,34,34,0.1)] hover:border-[rgba(204,34,34,0.4)]',
                'hover:shadow-[0_0_0_1px_rgba(204,34,34,0.1)]'
              )}
            >
              {/* Globe icon with ping dot */}
              <span className="relative w-[22px] h-[22px] shrink-0">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  className="opacity-55 group-hover/globe:opacity-90 transition-opacity duration-200"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="9"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-white/50 group-hover/globe:text-[#cc3333]"
                  />
                  <ellipse
                    cx="11"
                    cy="11"
                    rx="5"
                    ry="9"
                    stroke="currentColor"
                    strokeWidth="0.8"
                    className="text-white/35 group-hover/globe:text-[#cc3333]"
                  />
                  <line
                    x1="2"
                    y1="11"
                    x2="20"
                    y2="11"
                    stroke="currentColor"
                    strokeWidth="0.8"
                    className="text-white/35 group-hover/globe:text-[#cc3333]"
                  />
                  <path
                    d="M4 7 Q11 9 18 7"
                    stroke="currentColor"
                    strokeWidth="0.7"
                    fill="none"
                    className="text-white/25 group-hover/globe:text-[#cc3333]"
                  />
                  <path
                    d="M4 15 Q11 13 18 15"
                    stroke="currentColor"
                    strokeWidth="0.7"
                    fill="none"
                    className="text-white/25 group-hover/globe:text-[#cc3333]"
                  />
                </svg>
                {/* Ping dot */}
                <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-red-primary group-hover/globe:shadow-[0_0_0_3px_rgba(204,34,34,0.2)] transition-shadow duration-200" />
              </span>

              {/* Count */}
              <span className="text-[13px] font-bold text-white/75 group-hover/globe:text-[#f0ede8] transition-colors duration-200 leading-none">
                {countriesRecorded}
              </span>
              <span className="text-[11px] text-white/30 group-hover/globe:text-white/50 transition-colors duration-200 leading-none">
                {countriesRecorded === 1 ? 'country' : 'countries'}
              </span>

              {/* Hover reveal: "· View Globe →" */}
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[rgba(204,34,34,0.85)] max-w-0 group-hover/globe:max-w-[80px] overflow-hidden whitespace-nowrap opacity-0 group-hover/globe:opacity-100 transition-all duration-200">
                · View Globe
                <ArrowRight className="w-[9px] h-[9px]" />
              </span>
            </Link>
          )}

          {isGuest && (
            <UnlockCta
              label="Unlock everything"
              source="creator_header"
              size="sm"
              className="ml-1"
              {...(getChannelHandle(creator) !== undefined
                ? {
                    href: getChoosePlanHrefForCreator(getChannelHandle(creator)),
                    handler: getChannelHandle(creator),
                  }
                : {})}
            />
          )}
        </div>
      </PageShell>

      {/* Bottom spacing — breathing room before tabs */}
      <div className="h-8 sm:h-10" />
    </section>
  );
}
